// React
import { memo, useCallback, useMemo } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getUnitsOfMeasureAPI } from "services/api/finance/lookups/api";
import { DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// UI
import { TableSearch } from "ui/components/Table/TableSearch";

import { INCOMING_INVOICE_ITEMS_COLUMNS, INCOMING_INVOICE_ITEM_CHILD_COLUMNS } from "./consts";
import InvoiceIncomingItemRowChild from "./InvoiceIncomingItemRowChild";
import { InvoiceIncomingItemsProps } from "./types";

const THIRTY_MINUTES = 30 * 60 * 1000;

const InvoiceIncomingItems: React.FC<InvoiceIncomingItemsProps> = ({ items, allocations, isAllocatable }) => {
  const { t } = useTranslate();

  const { data: units } = useDropdownCache(
    DropdownCacheKey.UnitsOfMeasure,
    getUnitsOfMeasureAPI,
    THIRTY_MINUTES
  );

  // Stavka nosi šifru jedinice mjere, a u tablici treba simbol (kom, h, kg...).
  const unitSymbols = useMemo(
    () => (units ?? []).reduce(
      (acc: Record<string, string>, unit: any) => ({ ...acc, [unit.id]: unit.symbol ?? unit.name }),
      {}
    ),
    [units]
  );

  const rows = useMemo(
    () => items.map(item => ({ ...item, childRows: item.note ? [item] : [] })),
    [items]
  );

  const allocatedItemIds = useMemo(
    () => new Set(allocations.filter(allocation => allocation.vehicleId || allocation.expenseTypeId).map(allocation => allocation.invoiceIncomingItemId)),
    [allocations]
  );

  // Stavka bez troška na vozilu je posao koji čeka — ali tek kad je račun odobren.
  const resolveRowClassName = useCallback(
    (row: any) => !isAllocatable || allocatedItemIds.has(row.id) ? "" : "!bg-amber-50",
    [allocatedItemIds, isAllocatable]
  );

  return (
    <div className="p-3">
      {items.length > 0 && (
        <TableSearch
          columns={INCOMING_INVOICE_ITEMS_COLUMNS(t, unitSymbols)}
          data={rows}
          isLoading={false}
          selectableRows={false}
          columnsChild={INCOMING_INVOICE_ITEM_CHILD_COLUMNS()}
          CustomTableRowChild={InvoiceIncomingItemRowChild}
          rowClassName={resolveRowClassName}
        />
      )}
    </div>
  );
};

export default memo(InvoiceIncomingItems);
